/**
 * Participant vibe persistence.
 *
 * Path: events/{eventId}/vibePreferences/{uid}
 * Snapshot: events/{eventId}/vibeSnapshots/{snapshotId}
 * Pointer: events/{eventId}/vibeState/current
 *
 * Vocabulary IDs are checked in application code (selection / aggregate).
 * Firestore rules enforce ownership, shape, and size bounds so a later
 * custom category does not require a rules migration just to add an ID.
 *
 * Crowd listeners are opt-in. A guest who has not saved must not subscribe
 * to the preference collection.
 *
 * UPDATE VIBE writes an immutable snapshot plus the current pointer.
 * It does not store an interpretation or a recommended track.
 * A later phase can attach those beside the snapshot, for example
 * vibeInterpretations/{snapshotId}, without editing the snapshot.
 */

import {
  collection,
  doc,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../firebase';
import { VIBE_SCHEMA_VERSION } from './definitions';
import { buildVibeSnapshotPayload } from './aggregate';
import { canonicalizeVibeSelection, isCompleteVibeSelection } from './selection';

export function vibePreferenceRef(eventId, uid) {
  return doc(db, 'events', eventId, 'vibePreferences', uid);
}

export function vibePreferencesCollection(eventId) {
  return collection(db, 'events', eventId, 'vibePreferences');
}

export function vibeSnapshotRef(eventId, snapshotId) {
  return doc(db, 'events', eventId, 'vibeSnapshots', snapshotId);
}

export function vibeStateRef(eventId) {
  return doc(db, 'events', eventId, 'vibeState', 'current');
}

export function createSnapshotId(now = Date.now()) {
  const entropy = Math.random().toString(36).slice(2, 10);
  return `vibe_${now.toString(36)}_${entropy}`;
}

export function buildVibePreference(selection, {
  submittedAt = Date.now(),
  updatedAt = submittedAt,
} = {}) {
  const canonical = canonicalizeVibeSelection(selection);
  if (!isCompleteVibeSelection(canonical)) {
    return null;
  }
  return {
    schemaVersion: VIBE_SCHEMA_VERSION,
    feelId: canonical.feelId,
    styleIds: canonical.styleIds,
    flavorIds: canonical.flavorIds,
    submittedAt,
    updatedAt,
  };
}

export async function saveVibePreference(eventId, uid, selection, previous) {
  const now = Date.now();
  const payload = buildVibePreference(selection, {
    submittedAt: previous?.submittedAt || now,
    updatedAt: now,
  });
  if (!payload) {
    const error = new Error('Choose how it should feel');
    error.code = 'missing-feel';
    throw error;
  }
  await runTransaction(db, async (transaction) => {
    transaction.set(vibePreferenceRef(eventId, uid), payload);
  });
  return payload;
}

export function watchOwnVibePreference(eventId, uid, onChange) {
  if (!eventId || !uid) return () => {};
  return onSnapshot(
    vibePreferenceRef(eventId, uid),
    (snapshot) => {
      if (!snapshot?.exists?.()) {
        onChange(null);
        return;
      }
      onChange(snapshot.data() || null);
    },
    () => onChange(null),
  );
}

/**
 * Live saved preferences and the current snapshot pointer.
 * Call only for a host, or for a guest who already has a saved preference.
 */
export function watchCrowdVibe(eventId, onChange) {
  if (!eventId) return () => {};
  const stopPreferences = onSnapshot(
    vibePreferencesCollection(eventId),
    (snapshot) => {
      const preferencesByUid = {};
      (snapshot?.docs || []).forEach((item) => {
        preferencesByUid[item.id] = item.data();
      });
      onChange({ preferencesByUid });
    },
    () => onChange({ preferencesByUid: {}, denied: true }),
  );
  const stopState = onSnapshot(
    vibeStateRef(eventId),
    (snapshot) => {
      onChange({ vibeState: snapshot?.exists?.() ? (snapshot.data() || null) : null });
    },
    () => onChange({ vibeState: null }),
  );
  return () => {
    if (typeof stopPreferences === 'function') stopPreferences();
    if (typeof stopState === 'function') stopState();
  };
}

/**
 * Host-only snapshot of currently saved crowd state.
 * Unsaved drafts are absent because they are not in preferencesByUid.
 * A second call writes a new snapshot id and moves the pointer.
 * This function never updates or deletes an existing snapshot.
 */
export async function publishVibeSnapshot({
  eventId,
  hostUid,
  participants,
  presenceByUid,
  preferencesByUid,
  now = Date.now(),
  snapshotId = createSnapshotId(now),
}) {
  const payload = buildVibeSnapshotPayload({
    hostUid,
    now,
    participants,
    presenceByUid,
    preferencesByUid,
  });
  await runTransaction(db, async (transaction) => {
    transaction.set(vibeSnapshotRef(eventId, snapshotId), payload);
    transaction.set(vibeStateRef(eventId), {
      currentSnapshotId: snapshotId,
      updatedAt: now,
      updatedBy: hostUid,
    });
  });
  return {
    snapshotId,
    payload,
    interpretation: null,
    recommendedTrack: null,
  };
}
